import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import LanguageSwitcher from './LanguageSwitcher'
import { getIcon } from './icons/serviceIcons'
import { IconArrowRight, IconChevronDown, IconClose, IconMail, IconPhone } from './ui/Icon'
import RegMark from './ui/RegMark'
import { HOME_BY_LANG, alternatesFor, hasOwnRfq, normalizePath } from '../lib/routes'
import './navbar.css'

// Copy for every navbar string. Labels are the existing site copy; the three
// service blurbs condense each service page's own description.
const translations = {
    tr: {
        home: '/',
        contact: 'İletişim',
        blog: 'Blog/Haberler',
        blogPath: '/blog',
        cta: 'Teklif İste',
        corporate: 'Kurumsal',
        solutions: 'Çözümlerimiz',
        menu: 'Menü',
        closeMenu: 'Menüyü kapat',
        primaryNav: 'Ana gezinme',
        language: 'Dil',
        corporateDropdown: [
            { label: 'Hakkımızda', href: '#about', homeAnchor: true },
            { label: 'Vizyon & Misyon', href: '/kurumsal#vizyon' },
            { label: 'Değerlerimiz', href: '/kurumsal#degerler' },
            { label: 'Kalite Politikamız', href: '/kurumsal#kalite' },
            { label: 'Sertifikalarımız', href: '/kurumsal#sertifikalar' },
        ],
        solutionsDropdown: [
            { label: 'Tampon Baskı', to: '/tampon-baski', icon: 'pad', blurb: '3D ve kavisli yüzeylere hassas baskı' },
            { label: 'Serigrafi Baskı', to: '/serigrafi', icon: 'screen', blurb: 'Geniş ve düz yüzeylerde yüksek hacimli baskı' },
            { label: 'Montaj & Trim', to: '/montaj-trim', icon: 'assembly', blurb: 'Çok bileşenli parça montajı ve dekoratif trim' },
        ],
    },
    en: {
        home: '/en',
        contact: 'Contact',
        blog: 'Blog/News',
        blogPath: '/en/blog',
        cta: 'Get a Quote',
        corporate: 'Corporate',
        solutions: 'Solutions',
        menu: 'Menu',
        closeMenu: 'Close menu',
        primaryNav: 'Main navigation',
        language: 'Language',
        corporateDropdown: [
            { label: 'About Us', href: '#about', homeAnchor: true },
            { label: 'Vision & Mission', href: '/en/corporate#vision' },
            { label: 'Our Values', href: '/en/corporate#values' },
            { label: 'Quality Policy', href: '/en/corporate#quality' },
            { label: 'Certificates', href: '/en/corporate#certificates' },
        ],
        solutionsDropdown: [
            { label: 'Pad Printing', to: '/en/pad-printing', icon: 'pad', blurb: 'Precise printing on 3D and curved surfaces' },
            { label: 'Screen Printing', to: '/en/screen-printing', icon: 'screen', blurb: 'High-volume printing on large, flat surfaces' },
            { label: 'Assembly & Trim', to: '/en/assembly-trim', icon: 'assembly', blurb: 'Multi-component assembly and decorative trim' },
        ],
    },
    de: {
        home: '/de',
        contact: 'Kontakt',
        blog: 'Blog/Nachrichten',
        blogPath: '/de/blog',
        cta: 'Angebot Anfordern',
        corporate: 'Unternehmen',
        solutions: 'Lösungen',
        menu: 'Menü',
        closeMenu: 'Menü schließen',
        primaryNav: 'Hauptnavigation',
        language: 'Sprache',
        corporateDropdown: [
            { label: 'Über Uns', href: '#about', homeAnchor: true },
            { label: 'Vision & Mission', href: '/de/unternehmen#vision' },
            { label: 'Unsere Werte', href: '/de/unternehmen#werte' },
            { label: 'Qualitätspolitik', href: '/de/unternehmen#qualitaet' },
            { label: 'Zertifikate', href: '/de/unternehmen#zertifikate' },
        ],
        solutionsDropdown: [
            { label: 'Tampondruck', to: '/de/tampondruck', icon: 'pad', blurb: 'Präziser Druck auf 3D- und gewölbten Flächen' },
            { label: 'Siebdruck', to: '/de/siebdruck', icon: 'screen', blurb: 'Druck in hohen Stückzahlen auf großen, ebenen Flächen' },
            { label: 'Montage & Trim', to: '/de/montage-trim', icon: 'assembly', blurb: 'Montage mehrteiliger Baugruppen und Zierteile' },
        ],
    },
}

const HOVER_CLOSE_DELAY_MS = 180
const SCROLLED_THRESHOLD_PX = 8
const DESKTOP_QUERY = '(min-width: 1120px)'

/**
 * Resolve an in-page anchor against the locale home when we are not on it.
 *
 * @param {string} hash e.g. '#about'
 * @param {boolean} onHome
 * @param {string} home
 */
function anchorHref(hash, onHome, home) {
    if (onHome) return hash
    return home === '/' ? `/${hash}` : `${home}${hash}`
}

/**
 * Site navigation shared by every page.
 *
 * @param {{ lang?: 'tr' | 'en' | 'de', langLinks?: { tr: string, en: string, de: string } }} props
 *   `langLinks` overrides the automatic locale-equivalent mapping (kept for callers that pass it).
 */
export default function Navbar({ lang = 'tr', langLinks }) {
    const t = translations[lang] || translations.tr
    const { pathname } = useLocation()
    const path = normalizePath(pathname)
    const home = HOME_BY_LANG[lang] || '/'
    const onHome = path === normalizePath(home)
    const resolvedLangLinks = langLinks ?? alternatesFor(path) ?? HOME_BY_LANG
    const contactHref = hasOwnRfq(path) ? '#contact' : anchorHref('#contact', false, home)

    // Open state is stored with the path it was opened on, so a route change
    // closes the sheet and the panels without an effect.
    const [menuOpenAt, setMenuOpenAt] = useState(null)
    const [dropdown, setDropdown] = useState({ name: null, path: null })
    const isMenuOpen = menuOpenAt === pathname
    const openDropdown = dropdown.path === pathname ? dropdown.name : null
    const setOpenDropdown = useCallback((name) => setDropdown({ name, path: pathname }), [pathname])
    const [mobileExpanded, setMobileExpanded] = useState({})
    const [isScrolled, setIsScrolled] = useState(false)

    const closeTimerRef = useRef(null)
    const navRef = useRef(null)
    const toggleRef = useRef(null)
    const sheetRef = useRef(null)
    const idBase = useId()
    const panelId = (name) => `${idBase}-${name}`

    const closeMenu = useCallback(() => {
        setMenuOpenAt(null)
        setMobileExpanded({})
        setDropdown({ name: null, path: null })
    }, [])

    // Scroll state: one boolean, flipped only when the threshold is crossed.
    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > SCROLLED_THRESHOLD_PX)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    // Outside click + Escape close the desktop panels.
    useEffect(() => {
        const onPointer = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) setOpenDropdown(null)
        }
        const onKey = (e) => {
            if (e.key !== 'Escape') return
            setOpenDropdown(null)
            if (isMenuOpen) {
                closeMenu()
                toggleRef.current?.focus()
            }
        }
        document.addEventListener('pointerdown', onPointer)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('pointerdown', onPointer)
            document.removeEventListener('keydown', onKey)
        }
    }, [isMenuOpen, closeMenu, setOpenDropdown])

    // Mobile sheet: lock page scroll while open, move focus into the sheet and
    // make the rest of the page inert, so Tab cannot wander behind the sheet.
    useEffect(() => {
        if (!isMenuOpen) return undefined
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const header = navRef.current
        const sheet = sheetRef.current
        const behind = header?.parentElement
            ? [...header.parentElement.children].filter((el) => el !== header && el !== sheet && !el.inert)
            : []
        behind.forEach((el) => { el.inert = true })
        sheet?.querySelector('a, button')?.focus()
        const mq = window.matchMedia(DESKTOP_QUERY)
        const onChange = (e) => {
            if (e.matches) closeMenu()
        }
        mq.addEventListener('change', onChange)
        return () => {
            document.body.style.overflow = previous
            behind.forEach((el) => { el.inert = false })
            mq.removeEventListener('change', onChange)
        }
    }, [isMenuOpen, closeMenu])

    const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches

    const openOnHover = (name) => {
        if (!isDesktop()) return
        clearTimeout(closeTimerRef.current)
        setOpenDropdown(name)
    }

    const closeOnLeave = () => {
        if (!isDesktop()) return
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = setTimeout(() => setOpenDropdown(null), HOVER_CLOSE_DELAY_MS)
    }

    useEffect(() => () => clearTimeout(closeTimerRef.current), [])

    const toggleDropdown = (name) => setOpenDropdown(openDropdown === name ? null : name)

    const toggleMobileAccordion = (name) =>
        setMobileExpanded((prev) => ({ ...prev, [name]: !prev[name] }))

    const corporateItems = t.corporateDropdown.map((item) => ({
        ...item,
        href: item.homeAnchor ? anchorHref(item.href, onHome, home) : item.href,
    }))

    return (
        <>
            <header
                className="navbar"
                data-scrolled={isScrolled || undefined}
                data-menu-open={isMenuOpen || undefined}
                ref={navRef}
            >
                <div className="navbar-inner">
                    <Link to={t.home} className="logo" onClick={closeMenu} viewTransition>
                        <img
                            src="/anka-logo-full.webp"
                            srcSet="/anka-logo-full-220w.webp 220w, /anka-logo-full.webp 380w"
                            sizes="(max-width: 768px) 112px, 150px"
                            alt="Ankaplastik Logo"
                            className="logo-image"
                            width="190"
                            height="56"
                            fetchPriority="high"
                        />
                    </Link>

                    <nav className="nav-primary" aria-label={t.primaryNav}>
                        <ul className="nav-links">
                            <li
                                className="nav-dropdown"
                                data-open={openDropdown === 'corporate' || undefined}
                                onMouseEnter={() => openOnHover('corporate')}
                                onMouseLeave={closeOnLeave}
                            >
                                <button
                                    type="button"
                                    className="nav-trigger"
                                    aria-expanded={openDropdown === 'corporate'}
                                    aria-controls={panelId('corporate')}
                                    onClick={() => toggleDropdown('corporate')}
                                >
                                    {t.corporate}
                                    <IconChevronDown className="nav-chevron" size={16} />
                                </button>
                                <div className="nav-panel nav-panel--list" id={panelId('corporate')}>
                                    <ul>
                                        {corporateItems.map((item) => (
                                            <li key={item.href}>
                                                <a href={item.href} className="nav-panel-link" onClick={() => setOpenDropdown(null)}>
                                                    {item.label}
                                                    <IconArrowRight size={16} />
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </li>

                            <li
                                className="nav-dropdown"
                                data-open={openDropdown === 'solutions' || undefined}
                                onMouseEnter={() => openOnHover('solutions')}
                                onMouseLeave={closeOnLeave}
                            >
                                <button
                                    type="button"
                                    className="nav-trigger"
                                    aria-expanded={openDropdown === 'solutions'}
                                    aria-controls={panelId('solutions')}
                                    onClick={() => toggleDropdown('solutions')}
                                >
                                    {t.solutions}
                                    <IconChevronDown className="nav-chevron" size={16} />
                                </button>
                                <div className="nav-panel nav-panel--services" id={panelId('solutions')}>
                                    <ul>
                                        {t.solutionsDropdown.map((item) => {
                                            const Icon = getIcon(item.icon)
                                            const current = path === item.to
                                            return (
                                                <li key={item.to}>
                                                    <Link
                                                        to={item.to}
                                                        className="nav-service"
                                                        aria-current={current ? 'page' : undefined}
                                                        onClick={() => setOpenDropdown(null)}
                                                        viewTransition
                                                    >
                                                        <span className="nav-service-icon frame">
                                                            {Icon ? <Icon size={30} strokeWidth={1.5} /> : null}
                                                        </span>
                                                        <span className="nav-service-text">
                                                            <span className="nav-service-name">
                                                                {item.label}
                                                                {current ? <RegMark size={14} /> : null}
                                                            </span>
                                                            <span className="nav-service-blurb">{item.blurb}</span>
                                                        </span>
                                                        <IconArrowRight className="nav-service-arrow" size={18} />
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </li>

                            <li>
                                <a href={contactHref} className="nav-link">{t.contact}</a>
                            </li>
                            <li>
                                <Link
                                    to={t.blogPath}
                                    className="nav-link"
                                    aria-current={path.startsWith(t.blogPath) ? 'page' : undefined}
                                    viewTransition
                                >
                                    {t.blog}
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    <div className="nav-actions">
                        <LanguageSwitcher currentLang={lang} links={resolvedLangLinks} label={t.language} />
                        <a href={contactHref} className="btn btn--sm nav-cta">
                            {t.cta}
                            <IconArrowRight />
                        </a>
                        <button
                            type="button"
                            className="mobile-menu-toggle"
                            ref={toggleRef}
                            aria-expanded={isMenuOpen}
                            aria-controls={panelId('mobile')}
                            aria-label={isMenuOpen ? t.closeMenu : t.menu}
                            onClick={() => setMenuOpenAt(isMenuOpen ? null : pathname)}
                        >
                            <span className="mobile-menu-toggle-bars" aria-hidden="true">
                                <span />
                                <span />
                            </span>
                        </button>
                    </div>
                </div>
                <span className="nav-progress" aria-hidden="true" />
            </header>

            {/* Mobile sheet. Always in the DOM (prerendered copy), inert while closed. */}
            <div
                className="mobile-menu"
                id={panelId('mobile')}
                data-open={isMenuOpen || undefined}
                inert={!isMenuOpen}
                ref={sheetRef}
            >
                <div className="mobile-menu-scroll">
                    <ul className="mobile-nav-links">
                        <li className={`mobile-nav-item ${mobileExpanded.solutions ? 'expanded' : ''}`}>
                            <button
                                type="button"
                                className="mobile-nav-link"
                                aria-expanded={Boolean(mobileExpanded.solutions)}
                                onClick={() => toggleMobileAccordion('solutions')}
                            >
                                {t.solutions}
                                <IconChevronDown className="nav-chevron" size={22} />
                            </button>
                            <div className="mobile-accordion">
                                <ul>
                                    {t.solutionsDropdown.map((item) => {
                                        const Icon = getIcon(item.icon)
                                        return (
                                            <li key={item.to}>
                                                <Link to={item.to} className="mobile-sub-link mobile-sub-link--service" onClick={closeMenu}>
                                                    <span className="nav-service-icon frame">
                                                        {Icon ? <Icon size={24} strokeWidth={1.5} /> : null}
                                                    </span>
                                                    <span>
                                                        <span className="mobile-sub-title">{item.label}</span>
                                                        <span className="mobile-sub-blurb">{item.blurb}</span>
                                                    </span>
                                                </Link>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        </li>

                        <li className={`mobile-nav-item ${mobileExpanded.corporate ? 'expanded' : ''}`}>
                            <button
                                type="button"
                                className="mobile-nav-link"
                                aria-expanded={Boolean(mobileExpanded.corporate)}
                                onClick={() => toggleMobileAccordion('corporate')}
                            >
                                {t.corporate}
                                <IconChevronDown className="nav-chevron" size={22} />
                            </button>
                            <div className="mobile-accordion">
                                <ul>
                                    {corporateItems.map((item) => (
                                        <li key={item.href}>
                                            <a href={item.href} className="mobile-sub-link" onClick={closeMenu}>
                                                {item.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </li>

                        <li className="mobile-nav-item">
                            <a href={contactHref} className="mobile-nav-link" onClick={closeMenu}>
                                {t.contact}
                            </a>
                        </li>
                        <li className="mobile-nav-item">
                            <Link to={t.blogPath} className="mobile-nav-link" onClick={closeMenu}>
                                {t.blog}
                            </Link>
                        </li>
                    </ul>

                    <div className="mobile-menu-footer">
                        <LanguageSwitcher currentLang={lang} links={resolvedLangLinks} label={t.language} />
                        <a href={contactHref} className="btn btn--block mobile-cta-btn" onClick={closeMenu}>
                            {t.cta}
                            <IconArrowRight />
                        </a>
                        <div className="mobile-menu-contact">
                            <a href="tel:+902249999112">
                                <IconPhone size={18} />
                                +90 224 999 91 12
                            </a>
                            <a href="mailto:info@ankaplastik.com">
                                <IconMail size={18} />
                                info@ankaplastik.com
                            </a>
                        </div>
                    </div>
                </div>
                <button type="button" className="sr-only" onClick={closeMenu}>
                    <IconClose />
                    {t.closeMenu}
                </button>
            </div>
        </>
    )
}
